import { useRef, useState } from "react";
import { api } from "../lib/apiClient";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from "./ui/dialog";
import { Upload, FileDown, FileSpreadsheet, AlertTriangle, ArrowLeft, CheckCircle2, Mail } from "lucide-react";

const TAB_LABELS = {
  ffe: "FF&E", budget: "Budget", note: "Notes", time: "Time",
  product: "Products", approval: "Approvals", task: "Tasks",
  event: "Calendar", document: "Documents", photo: "Photos", po: "POs",
};

export default function ImportProjectsDialog({ onImported }) {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState("intro"); // intro | preview | done
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [result, setResult] = useState(null);
  const [emailSummary, setEmailSummary] = useState(false);
  const fileRef = useRef(null);
  const pendingFile = useRef(null);

  const reset = () => {
    setStep("intro"); setBusy(false); setPreview(null); setResult(null); setEmailSummary(false); pendingFile.current = null;
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get("/project-import/template", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "workroom-import-template.csv";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 2000);
    } catch { toast.error("Could not download the template."); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/project-import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      pendingFile.current = file;
      setPreview(data);
      setStep("preview");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not read that file. Please check the format.");
    } finally { setBusy(false); }
  };

  const confirmImport = async () => {
    if (!pendingFile.current) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", pendingFile.current);
      fd.append("email_summary", emailSummary ? "true" : "false");
      const { data } = await api.post("/project-import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      setStep("done");
      toast.success(`Imported ${data.projects_created} project(s) and ${data.items_added} item(s).`);
      if (onImported) onImported();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Import failed. Please try again.");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="import-projects-btn" variant="outline"
          className="rounded-none border-[#A68A6B] text-[#2B2622] hover:bg-[#A68A6B] hover:text-white uppercase tracking-wider text-xs py-6 px-6">
          <Upload size={16} className="mr-2" strokeWidth={1.5} /> Import Projects
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-[#EBE5E0] bg-white max-w-lg max-h-[85vh] overflow-y-auto" data-testid="import-projects-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-[#2B2622]">
            {step === "done" ? "Import complete" : step === "preview" ? "Review before importing" : "Import projects"}
          </DialogTitle>
          <DialogDescription>
            {step === "intro" && "Bring projects over from any software. Works with exports from Ivy, Houzz Pro, DesignFiles, Studio Designer, Programa, spreadsheets — anything you can save as CSV."}
            {step === "preview" && "Here's how your file maps into WorkRoom. Confirm to create everything, or go back and adjust your CSV."}
            {step === "done" && "Everything below has been filed into the right tabs."}
          </DialogDescription>
        </DialogHeader>

        {step === "intro" && (
          <ol className="space-y-4 mt-2 text-sm text-[#2B2622]">
            <li className="flex gap-3">
              <span className="w-6 h-6 shrink-0 bg-[#EDE7DF] text-[#8C7154] flex items-center justify-center text-xs">1</span>
              <div className="flex-1">
                <p className="font-medium">Download the template</p>
                <p className="text-[#5C5651] text-[13px] mb-2">One row per item. Set the <strong>Type</strong> column (FF&E, Budget, Note, Time, Approval, Task, Event, Photo, Document, PO, Product) and group rows under the same Project Name.</p>
                <button data-testid="download-template-btn" onClick={downloadTemplate}
                  className="inline-flex items-center gap-2 border border-[#EBE5E0] px-3 py-2 text-xs uppercase tracking-wider text-[#5C5651] hover:border-[#A68A6B] hover:text-[#A68A6B]">
                  <FileDown size={14} className="text-[#A68A6B]" /> CSV template
                </button>
              </div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 shrink-0 bg-[#EDE7DF] text-[#8C7154] flex items-center justify-center text-xs">2</span>
              <div><p className="font-medium">Fill in your project data</p>
                <p className="text-[#5C5651] text-[13px]">Line items, budgets, tasks, calendar dates, notes, and links to photos, documents & POs. We file each row under the matching tab.</p></div>
            </li>
            <li className="flex gap-3">
              <span className="w-6 h-6 shrink-0 bg-[#EDE7DF] text-[#8C7154] flex items-center justify-center text-xs">3</span>
              <div className="flex-1">
                <p className="font-medium">Upload & preview</p>
                <p className="text-[#5C5651] text-[13px] mb-2">You'll see exactly what gets created before anything is saved.</p>
                <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,text/csv" className="hidden" onChange={onFile} />
                <Button data-testid="upload-csv-btn" onClick={() => fileRef.current?.click()} disabled={busy}
                  className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs">
                  <FileSpreadsheet size={15} className="mr-2" /> {busy ? "Reading…" : "Upload CSV"}
                </Button>
              </div>
            </li>
          </ol>
        )}

        {step === "preview" && preview && (
          <div className="mt-1 space-y-3" data-testid="import-preview">
            <p className="text-sm text-[#5C5651]">
              <strong className="text-[#2B2622]">{preview.project_count}</strong> project(s) found.
              {preview.total_unclassified > 0 && (
                <span className="text-[#B0724A]"> {preview.total_unclassified} row(s) need review.</span>
              )}
            </p>
            <div className="space-y-3">
              {preview.projects.map((p, i) => (
                <div key={i} className="border border-[#EBE5E0] p-3" data-testid={`import-preview-project-${i}`}>
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-serif text-lg text-[#2B2622]">{p.name}</p>
                    {p.exists
                      ? <span className="text-[11px] uppercase tracking-wide text-[#8C7154] bg-[#EDE7DF] px-2 py-0.5">Existing — items added</span>
                      : <span className="text-[11px] uppercase tracking-wide text-[#506B52] bg-[#EEF3EE] px-2 py-0.5">New project</span>}
                  </div>
                  {p.client_email && <p className="text-xs text-[#9b938b] mt-0.5">Client: {p.client_email}</p>}
                  <div className="flex flex-wrap gap-1.5 mt-2">
                    {Object.entries(p.counts).filter(([k, v]) => k !== "unclassified" && v > 0).map(([k, v]) => (
                      <span key={k} className="text-[11px] text-[#5C5651] border border-[#EBE5E0] px-2 py-0.5">
                        {v} {TAB_LABELS[k] || k}
                      </span>
                    ))}
                    {p.counts.unclassified > 0 && (
                      <span className="text-[11px] text-[#B0724A] border border-[#E5D3C6] bg-[#FAF3EE] px-2 py-0.5 inline-flex items-center gap-1">
                        <AlertTriangle size={11} /> {p.counts.unclassified} need review
                      </span>
                    )}
                  </div>
                  {p.budget_total > 0 && (
                    <p className="text-xs text-[#5C5651] mt-2">Budget total: <strong className="text-[#2B2622]">${p.budget_total.toLocaleString()}</strong></p>
                  )}
                </div>
              ))}
            </div>
            <div className="flex items-center justify-between gap-2 pt-1">
              <Button data-testid="import-back-btn" variant="ghost" onClick={reset} disabled={busy}
                className="rounded-none text-[#5C5651] hover:text-[#2B2622] uppercase tracking-wider text-xs">
                <ArrowLeft size={14} className="mr-1" /> Back
              </Button>
              <Button data-testid="import-confirm-btn" onClick={confirmImport} disabled={busy}
                className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs">
                {busy ? "Importing…" : "Confirm & import"}
              </Button>
            </div>
            <label data-testid="import-email-summary-toggle" htmlFor="import-email-summary"
              className="flex items-center gap-2 text-[13px] text-[#5C5651] cursor-pointer select-none pt-1">
              <input id="import-email-summary" type="checkbox" checked={emailSummary}
                onChange={(e) => setEmailSummary(e.target.checked)}
                className="h-4 w-4 accent-[#A68A6B] cursor-pointer" />
              <Mail size={14} className="text-[#A68A6B]" /> Email me an import summary
            </label>
          </div>
        )}

        {step === "done" && result && (
          <div className="mt-2 space-y-3" data-testid="import-result">
            <div className="border border-[#E4EDE4] bg-[#F3F7F3] p-4 text-sm text-[#506B52] flex items-start gap-2">
              <CheckCircle2 size={18} className="shrink-0 mt-0.5" />
              <span>Imported <strong>{result.projects_created}</strong> new project(s) and <strong>{result.items_added}</strong> item(s) across your tabs.</span>
            </div>
            {result.flagged > 0 && (
              <div className="border border-[#E5D3C6] bg-[#FAF3EE] p-4 text-sm text-[#8a5a3a] flex items-start gap-2">
                <AlertTriangle size={18} className="shrink-0 mt-0.5" />
                <span><strong>{result.flagged}</strong> row(s) couldn't be classified. Open each project's Overview tab to file them under the right tab.</span>
              </div>
            )}
            {result.emailed && (
              <div data-testid="import-emailed-note" className="border border-[#EBE5E0] bg-[#F9F8F6] p-3 text-[13px] text-[#5C5651] flex items-center gap-2">
                <Mail size={15} className="text-[#A68A6B]" /> A summary is on its way to your inbox.
              </div>
            )}
            <Button data-testid="import-done-btn" onClick={() => { setOpen(false); reset(); }}
              className="rounded-none bg-[#A68A6B] hover:bg-[#8C7154] text-white uppercase tracking-wider text-xs w-full">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
